/**
 * PriorityDetailCard.tsx
 *
 * Renders the `priority_detail` envelope data emitted by onething_get_priority.
 * Reuses priorityCardTokens for status/type styling — no new colors.
 */

import { AlertTriangle, Crown } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  getPriorityStatusMeta,
  priorityTypeBadgeClass,
  priorityTypeAccentClass,
} from "@/components/priorities/priorityCardTokens";
import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { PriorityStatus, PriorityType } from "@/types";

// ---------------------------------------------------------------------------
// Data shape for kind "priority_detail"
// ---------------------------------------------------------------------------

export interface PriorityDetailAssignment {
  name: string;
  isDri: boolean;
  assignmentStatus: PriorityStatus;
  icTask?: string;
  blockedReason?: string;
  blocker: { pid: string; fullName: string } | null;
}

export interface PriorityDetail {
  pid: string;
  code: string;
  type: PriorityType;
  name: string;
  description?: string;
  priorityStatus: PriorityStatus;
  driName: string | null;
  assignments: PriorityDetailAssignment[];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AssignmentRow({ a }: { a: PriorityDetailAssignment }) {
  const statusMeta = getPriorityStatusMeta(a.assignmentStatus);
  const StatusIcon = statusMeta.icon;

  return (
    <div className="mx-1 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted/50 cursor-default">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[12.5px] font-semibold tracking-tight">{a.name}</span>
          {a.isDri && (
            <Badge variant="outline" className="gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 border bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800">
              <Crown className="h-2.5 w-2.5" />
              DRI
            </Badge>
          )}
          <PriorityStatusTooltip status={a.assignmentStatus}>
            <Badge
              variant="outline"
              className={`gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 border ${statusMeta.className}`}
            >
              <StatusIcon className="h-2.5 w-2.5" />
              {statusMeta.label}
            </Badge>
          </PriorityStatusTooltip>
        </div>
        {a.icTask && (
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{a.icTask}</p>
        )}
        {a.blocker && (
          <div className="mt-1 flex items-start gap-1.5 rounded-md bg-red-50 dark:bg-red-950/60 px-2 py-1 text-[10.5px] text-red-700 dark:text-red-300 leading-relaxed">
            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              Blocked by {a.blocker.fullName}
              {a.blockedReason ? `: ${a.blockedReason}` : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface PriorityDetailCardProps {
  data: PriorityDetail;
}

export function PriorityDetailCard({ data }: PriorityDetailCardProps) {
  const statusMeta = getPriorityStatusMeta(data.priorityStatus);
  const StatusIcon = statusMeta.icon;

  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      {/* Type accent bar — full-width 3px pill */}
      <div className={`h-[3px] w-full ${priorityTypeAccentClass[data.type]}`} />

      <CardHeader className="px-4 pt-3.5 pb-2">
        {/* Code + type + status badges */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">{data.code}</span>
          <Badge
            variant="outline"
            className={`rounded-full text-[10.5px] font-semibold px-2 py-0.5 border ${priorityTypeBadgeClass[data.type]}`}
          >
            {data.type}
          </Badge>
          <PriorityStatusTooltip status={data.priorityStatus}>
            <Badge
              variant="outline"
              className={`gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 border ${statusMeta.className}`}
            >
              <StatusIcon className="h-2.5 w-2.5" />
              {statusMeta.label}
            </Badge>
          </PriorityStatusTooltip>
        </div>
        {/* Name */}
        <p className="text-[15px] font-bold leading-snug tracking-tight">{data.name}</p>
        {/* Description */}
        {data.description && (
          <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">
            {data.description}
          </p>
        )}
        {/* DRI */}
        {data.driName && (
          <div className="flex items-center gap-1.5 mt-1.5 text-[11.5px] text-muted-foreground">
            <Crown className="h-3 w-3" />
            <span>
              DRI · <span className="font-semibold text-foreground">{data.driName}</span>
            </span>
          </div>
        )}
      </CardHeader>

      {data.assignments.length > 0 && (
        <>
          <div className="flex items-center justify-between border-t border-border/50 px-4 py-2">
            <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
              Assignments
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
              {data.assignments.length}
            </span>
          </div>
          <CardContent className="px-2 pb-2 pt-0">
            {data.assignments.map((a, i) => (
              <AssignmentRow key={`${a.name}-${i}`} a={a} />
            ))}
          </CardContent>
        </>
      )}
    </Card>
  );
}
